// QnACodingRound.jsx
import React, { useMemo, useState } from "react";
import questions from "./qnaJSONdata/QnACodingRound.json";
import QnASideBarRight from "./QnASideBarRight";
import ScrollButton from "../../ScrollButton";

/**
 * Coding Round Q&A
 * - Left rail: ONLY Cheat-Sheets & Patterns (no profile/widgets)
 * - Top-right: Download PDF + Print
 * - Filter + per-question toggle + Show all / Hide all
 * - Clean Bootstrap 5 layout & accessible markup
 */

const CODING_PDF = "/pdfs/coding-round-questions.pdf"; // place file in /public/pdfs/
const normalize = (s = "") => s.toLowerCase();

/* =============== UI bits =============== */
function TopActions({ pdfHref }) {
  return (
    <div className="d-flex gap-2">
      <a
        href={pdfHref}
        download
        className="btn btn-outline-danger btn-sm"
        aria-label="Download Coding Round questions as PDF"
      >
        Download PDF
      </a>
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        onClick={() => window.print()}
        aria-label="Print this page"
      >
        Print
      </button>
    </div>
  );
}

function FilterBar({ value, onChange, shown, total, onShowAll, onHideAll }) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <input
        className="form-control form-control-sm"
        style={{ maxWidth: 420 }}
        type="search"
        placeholder="Filter (e.g., hashmap, two pointers, recursion, sliding window)…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Filter questions"
      />
      <span className="text-muted small">Showing {shown} of {total}</span>

      <div className="ms-auto d-flex gap-2">
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={onShowAll}
          aria-label="Show all answers"
        >
          Show all
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={onHideAll}
          aria-label="Hide all answers"
        >
          Hide all
        </button>
      </div>
    </div>
  );
}

function QAItem({ item, isOpen, onToggle }) {
  const answerId = `answer-${item.id}`;

  return (
    <li id={`q-${item.id}`} className="mb-4">
      {/* Question header + toggle */}
      <div className="d-flex align-items-start justify-content-between gap-3">
        <h5 className="mb-2 flex-grow-1">{item.question}</h5>

        <button
          type="button"
          className={`btn btn-sm ${isOpen ? "btn-outline-secondary" : "btn-primary"}`}
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={answerId}
        >
          {isOpen ? "Hide answer" : "Show answer"}
        </button>
      </div>

      {/* Answer + code + explanation (hidden by default) */}
      {isOpen && (
        <div id={answerId} className="mt-2">
          {item.answer ? (
            <div className="mb-2" style={{ whiteSpace: "pre-wrap" }}>
              {item.answer}
            </div>
          ) : null}

          {item.code ? (
            <pre
              className="p-3 bg-light rounded border mt-2"
              style={{ overflow: "auto", fontSize: "0.95rem" }}
            >
              <code>{item.code}</code>
            </pre>
          ) : null}

          {item.explanation ? (
            <>
              <p className="mt-3 mb-1 fw-semibold">Explanation</p>
              <div className="text-body" style={{ whiteSpace: "pre-wrap" }}>
                {item.explanation}
              </div>
            </>
          ) : null}
        </div>
      )}
    </li>
  );
}

/* =============== Left rail: ONLY Cheat-Sheets & Patterns =============== */
function CheatSheetRail({ onApplyFilter }) {
  const ITEMS = [
    { label: "Two Pointers", token: "two pointers" },
    { label: "Sliding Window", token: "sliding window" },
    { label: "HashMap / Set", token: "hashmap set" },
    { label: "Binary Search", token: "binary search" },
    { label: "Recursion / DFS", token: "recursion dfs" },
    { label: "BFS / Graph", token: "bfs graph" },
    { label: "Dynamic Programming", token: "dynamic programming dp" },
  ];

  return (
    // visible only on extra-wide screens; keeps mobile clean
    <aside className="d-none d-xl-block col-xl-2 pt-3" aria-label="Cheat-sheets and patterns">
      <div className="position-sticky" style={{ top: "1rem" }}>
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <h2 className="h6 text-uppercase text-muted mb-2">Cheat-Sheets & Patterns</h2>
            <div className="d-flex flex-wrap gap-2">
              {ITEMS.map((it) => (
                <button
                  key={it.label}
                  type="button"
                  className="btn btn-light btn-sm border"
                  onClick={() => onApplyFilter(it.token)}
                  aria-label={`Filter by ${it.label}`}
                >
                  {it.label}
                </button>
              ))}
            </div>

            <hr className="my-3" />

            {/* Point wherever your Big-O page lives */}
            <a
              className="btn btn-outline-dark btn-sm w-100"
              href="https://www.bigocheatsheet.com/"
              aria-label="Big-O quick table"
              target="_blank"
              rel="noreferrer"
            >
              Big-O quick table
            </a>
          </div>
        </div>
      </div>
    </aside>
  );
}

/* =============== Page =============== */
export default function QnACodingRound() {
  const [query, setQuery] = useState("");
  const [openMap, setOpenMap] = useState(() => Object.create(null)); // which questions are expanded

  const filtered = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return questions;
    return questions.filter((x) =>
      normalize(`${x.question} ${x.answer ?? ""} ${x.code ?? ""} ${x.explanation ?? ""}`).includes(qn)
    );
  }, [query]);

  const showAll = () => {
    const next = Object.create(null);
    filtered.forEach((q) => {
      next[q.id] = true;
    });
    setOpenMap(next);
  };

  const hideAll = () => {
    setOpenMap(Object.create(null));
  };

  const toggleOne = (id) => {
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: Cheat-Sheets only */}
        <CheatSheetRail onApplyFilter={(token) => setQuery(token)} />

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          <header className="mb-2 d-flex justify-content-between align-items-start">
            <div>
              <h1 className="h2 mb-1">Coding Round Interview Questions</h1>
              <p className="text-muted mb-0">
                Click “Show answer” to reveal the solution and explanation. Use the filter or the
                pattern shortcuts to find common techniques quickly.
              </p>
            </div>
            <TopActions pdfHref={CODING_PDF} />
          </header>

          <FilterBar
            value={query}
            onChange={setQuery}
            total={questions.length}
            shown={filtered.length}
            onShowAll={showAll}
            onHideAll={hideAll}
          />

          <section className="pt-2">
            {filtered.length === 0 ? (
              <div className="text-center text-muted py-5">
                No questions match your filter. Try a different keyword.
              </div>
            ) : (
              <ol className="ps-3">
                {filtered.map((item) => (
                  <QAItem
                    key={item.id}
                    item={item}
                    isOpen={!!openMap[item.id]}
                    onToggle={() => toggleOne(item.id)}
                  />
                ))}
              </ol>
            )}
          </section>

          <footer className="border-top mt-4 pt-3 small text-muted">
            Tip: State your approach, complexity, and edge cases (empty input, duplicates, large
            data, Unicode). If time remains, discuss alternative strategies.
          </footer>

          <ScrollButton />
        </main>

        {/* RIGHT: existing sidebar */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <QnASideBarRight />
        </aside>
      </div>
    </div>
  );
}
